/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable sonarjs/cognitive-complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { Heading } from '@tcp/core/styles/themes/TCP/typotheme';
import { v4 as uuidv4 } from 'uuid';
import { parseBoolean } from '@tcp/core/src/utils/utils';
import PlpTileView from '@tcp/core/src/components/features/browse/PlpTile/views/PlpTile.view';
import HeroTileView from '@tcp/core/src/components/features/browse/PlpTile/views/HeroTile.view';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import { plpTileClassName } from '@tcp/core/src/components/features/browse/PlpTile/plpTileConstants';
import { getTileHeight } from '@tcp/core/src/components/features/browse/CategoryListingPage/container/CategoryListingPage.utils';
import {
  HorizontalBar,
  PlpTileContianer,
  StyledPaginatedSkeleton,
  VertcalPromoGrid,
  StyledESpotPromoSkeleton,
} from '../../styles/PaginatedPage.style';
import GridPromo from '../../../../../common/molecules/GridPromo';
import CATEGORY_LISTING_PAGE_CONSTANTS from '../../container/CategoryListingPage.constants';

const PjpPlpTileView = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "plp-alt-tile" */ '@tcp/core/src/components/features/browse/PlpTile/views/PjpPlpTile.view'
  )
);

const AltPlpTile = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "plp-alt-tile" */ '@tcp/core/src/components/features/browse/PlpTile/views/AltPlpTile.view'
  )
);

const ModuleDynamic = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "moduleDynamic" */ '@tcp/core/src/components/common/molecules/ModuleDynamic'
    ),
  {
    ssr: false,
    loading: () => <StyledESpotPromoSkeleton />,
  }
);

// Return 0 if currentGridNo is null or undefined
const getPromoLength = (currentGridNo) =>
  currentGridNo ? Object.values(currentGridNo).reduce((acc, curr) => acc + curr.tileSize, 0) : 0;
const getPromoGridLength = (currentCrossGridNo) =>
  currentCrossGridNo ? Object.keys(currentCrossGridNo).length : 0;

const renderAddToBagLabel = (isBopisFilterOn) => {
  const plpTileLabels = getLabelFromCopyOrFromTranslation('PLP.plpTiles', PLPLabels);
  const pickupInStore = renderLabel(
    plpTileLabels.lbl_plp_pickup_in_store,
    'lbl_plp_pickup_in_store'
  );
  const addToBag = renderLabel(plpTileLabels.lbl_add_to_bag, 'lbl_add_to_bag');
  return isBopisFilterOn
    ? pickupInStore || CATEGORY_LISTING_PAGE_CONSTANTS.PICK_UP_IN_STORE
    : addToBag || CATEGORY_LISTING_PAGE_CONSTANTS.ADD_TO_BAG;
};

export const getInGridTileHeight = (isDoubleTile, gridPosition, gridBlockRef) => {
  if (!gridBlockRef?.children?.length) return { tileHeightValue: 0, tileBadgeHeightValue: 0 };
  // Determine grid columns based on viewport breakpoints
  // Default 4 for desktop, 3 for Tablet and 2 for Mobile
  const column2 = window.innerWidth <= 929 ? 3 : 4;
  const columns = window.innerWidth <= 534 ? 2 : column2;
  const rowIndex = Math.floor((gridPosition - 1) / columns);
  const rowStart = rowIndex * columns;
  const rowEnd = Math.min(rowStart + columns, gridBlockRef.children.length);

  // Step 1: Search for a valid tile in the same row (except the given gridPosition)
  // Step 2: For Mobile (2*2), Search for a valid tile in the same row (except the given gridPosition)
  for (let i = rowStart; i <= rowEnd; i += 1) {
    if (i + 1 !== gridPosition && gridBlockRef.children[i]?.classList?.contains(plpTileClassName)) {
      return getTileHeight(gridBlockRef.children[i], { rowEnd, currentIndex: i });
    }
  }
  // Step 3: Special case for isDoubleTile in Mobile (fallback to last two elements)
  if (isDoubleTile && columns === 2) {
    const targetElement =
      gridBlockRef.children[gridBlockRef.children.length - (gridPosition === 1 ? 1 : 2)];
    return getTileHeight(targetElement);
  }

  return { tileHeightValue: 0, tileBadgeHeightValue: 0, shouldAllowMaxHeight: true };
};

const checkForStartingRowIndex = ({
  adjustedIndex,
  tileSize,
  updatedPPLPLength,
  nearestCategoryStartingIndex,
}) => {
  if (adjustedIndex % tileSize === 1) {
    return { startingIndex: true, indexAdjustmentNeeded: 0 };
  }
  if (nearestCategoryStartingIndex === 0) {
    return {
      startingIndex: false,
      indexAdjustmentNeeded: (adjustedIndex - updatedPPLPLength) % tileSize,
    };
  }
  return { startingIndex: false, indexAdjustmentNeeded: adjustedIndex - updatedPPLPLength };
};

const getDoubleTileClassName = (provider, IS_NON_CLICKABLE_VIEW) => {
  if (provider === 'stylitics_carousel') {
    return parseBoolean(IS_NON_CLICKABLE_VIEW)
      ? 'fullWidthNonClickablePromo'
      : 'fullWidthMbShoByLookStylitics';
  }
  return '';
};

const getInGridFullRowHeight = (gridBlockRef) => {
  const children = gridBlockRef?.children;
  if (!children || children.length === 0) {
    return { fullRowHeight: 0 };
  }

  const childArray = Array.from(children).filter((item) =>
    item?.classList?.contains(plpTileClassName)
  );
  const targetIndex = childArray.findIndex(
    (child) =>
      child.classList.contains('fullWidthMbShoByLookStylitics') ||
      child.classList.contains('fullWidthNonClickablePromo')
  );

  const isEdgeIndex = targetIndex === 0 || targetIndex === childArray.length - 1;
  const fallbackIndex = isEdgeIndex ? childArray.length - 4 : 0;
  const fallbackElement = childArray[fallbackIndex];

  return {
    fullRowHeight: fallbackElement?.clientHeight || 0,
  };
};

const promoHandler = (
  cid,
  tileSize,
  promo,
  gridPosition,
  updatedPPLP,
  index,
  pageCategoryId,
  gridBlockRef,
  processedBreadCrumbs,
  titleTextCount
) => {
  const { provider = '', moduleName = '', IS_NON_CLICKABLE_VIEW = false } = promo;
  const isDoubleTile = tileSize === 2;
  const tempArray = [...updatedPPLP];
  const { tileHeightValue, tileBadgeHeightValue, shouldAllowMaxHeight } = getInGridTileHeight(
    isDoubleTile,
    gridPosition,
    gridBlockRef
  );
  const isShopByLookConfig =
    provider === 'stylitics_carousel' && !parseBoolean(IS_NON_CLICKABLE_VIEW);
  const doubleTileClassName = isShopByLookConfig ? 'doubleTile shoByLookStylitics' : 'doubleTile';
  const isGridPositionOdd =
    isDoubleTile && gridPosition % 2 !== 0 && index % 2 === 0 && gridPosition < updatedPPLP.length;

  let adjustedIndex = index;
  let itemBeforeDoubleTile = null;

  if (isGridPositionOdd && tempArray[adjustedIndex - 1]) {
    itemBeforeDoubleTile = tempArray.splice(adjustedIndex - titleTextCount);
    adjustedIndex -= titleTextCount;
  }
  if (titleTextCount === 0 && !isGridPositionOdd && gridPosition === index) {
    itemBeforeDoubleTile = tempArray.splice(adjustedIndex - 1);
    adjustedIndex -= 1;
  }

  tempArray[adjustedIndex] = (
    <VertcalPromoGrid
      key={`${cid}-promo-grid-${gridPosition}-${index}`}
      className={isDoubleTile ? doubleTileClassName : 'singleTileEspot'}
      isModuleContainer={moduleName === 'module_container'}
    >
      <GridPromo
        promoObj={promo}
        variation="vertical"
        isInGridPromo
        pageCategoryId={pageCategoryId}
        isSingleTile={tileSize === 1}
        isDoubleTile={isDoubleTile}
        tileHeightValue={tileHeightValue}
        tileBadgeHeightValue={tileBadgeHeightValue}
        processedBreadCrumbs={processedBreadCrumbs}
        shouldAllowMaxHeight={shouldAllowMaxHeight}
      />
    </VertcalPromoGrid>
  );
  if (isDoubleTile) {
    tempArray[adjustedIndex + 1] = (
      <div className={'empty-div-double-tile'} data-index={adjustedIndex + 1} />
    );
    if (itemBeforeDoubleTile) {
      tempArray.splice(adjustedIndex + 2, 0, ...itemBeforeDoubleTile);
    }
  }
  return tempArray;
};

// eslint-disable-next-line max-statements
export default function PaginatedView(props) {
  const {
    paginatedProducts,
    onQuickViewOpenClick,
    onAddItemToFavorites,
    onSetLastDeletedItemIdAction,
    wishListData,
    isBrierleyPromoEnabled,
    isShortImage,
    processedBreadCrumbs,
    isBopisFilterOn,
    promotionsData,
    serverTime,
    isBuyMorePromoEnabled,
    trackClickPromotion,
    pathName,
    trackWishListClick,
    isLoggedIn,
    saveProductDataForPdp,
    saveProductDataForQV,
    isPlcc,
    isInternationalShipping,
    isTopRatedBackAbEnabled,
    isBestSellerBackAbEnabled,
    isSyteEnabled,
    isNewSwatchUrlEnabled,
    isNewPLPAtbCardTileEnabled,
    isNewPLPImageBorderEnabled,
    isAlternatePlpEnabled,
    isPaginatedAlternatePlpEnabled,
    addToBagEcom,
    isOneClickATCEnabled,
    min,
    currentGridNo,
    currentCrossGridNo,
    currentPageNumber,
    isPaginatorClicked,
    cid,
    isHideOriginalPriceAndMarchantTagEnabled,
    isMostFavoritedBadgeEnabled,
    setPLPSLPAddToCartCTA,
    isInfluencerBadgeEnabled,
    isTileRedesignAB,
    isInfluencerPicksEnabled,
    favAddRemoveLoadingState,
    additionalProductsCountOnCsr,
    isAlternateImageOnHoverEnabled,
    isShowPriceRangeEnabled,
    isUpdatedPromotionsDisplayEnabled,
    imageConfigWithoutDPR,
    currentRowNo,
    recordsPerPage,
    isFullWidthInGridPromoHidden,
    isDynamicColorSwatchesEnabled,
    isHoverToPreviewColorSwatchesEnabled,
    isInteractiveColorSwatchBandEnabled,
    isHoverToRevealATBButtonEnabled,
    isFourTileGridEnabled,
    gridBlockRef,
    isSwatchAsColorUrlEnabled,
    isFeatureBrandEnabled,
    isPLPDynamicGridHeroEnabled,
    heroSlot,
    gridColumn,
    isRemoveCategoryBreakEnabled,
    isPLPSortAndOneHrFilterRedesignEnabled,
    isFinalSaleABEnabled,
    hidePromoOnFilterApplied,
    isRandomBgColorEnabled,
    isDynamicBadgeEnabledAbtest,
    isFadeImageHoverEnabled,
  } = props;
  const heroSlotRow = heroSlot?.length > 0 ? [...heroSlot] : [];
  const [tileHeight, setTileHeight] = useState(0);
  const pageCategoryId = cid;
  const itemTitle = isRemoveCategoryBreakEnabled ? 'item-title-no-show' : 'item-title';
  const handleHeightChange = (height) => setTileHeight(height);

  const tileActions = {
    onQuickViewOpenClick,
    onAddItemToFavorites,
    onSetLastDeletedItemIdAction,
    addToBagEcom,
  };

  const categoryName = processedBreadCrumbs
    ? processedBreadCrumbs[processedBreadCrumbs.length - 1]?.displayName
    : '';
  const totalProductGrids = paginatedProducts?.length;

  let categoryData = '';
  if (processedBreadCrumbs && processedBreadCrumbs.length > 0) {
    const { categoryId, displayName } = processedBreadCrumbs[processedBreadCrumbs.length - 1];
    categoryData = `${categoryId} | ${displayName}`;
  }

  // processedBreadCrumbs
  if (!totalProductGrids) return null;

  const promoLength = getPromoLength(currentGridNo);
  const promoLength2 = getPromoLength(currentRowNo);
  const promoGridLength = getPromoGridLength(currentCrossGridNo);
  const totalRcd = promoLength + totalProductGrids + promoGridLength + promoLength2;
  let updatedPPLP = [];
  let titleIndexesArray = [];
  let heroProductArray = [];
  let totalProductHero = 0;
  let titleTextCount = 0;
  for (
    let i = 0, k = 0, titleCount = 0, productIndex = 0;
    i < totalRcd && k < totalProductGrids;

  ) {
    const gridPosition = min + i + 1 - titleCount;

    if (
      !hidePromoOnFilterApplied &&
      currentGridNo &&
      !updatedPPLP[i] &&
      currentGridNo[gridPosition]
    ) {
      const promo = currentGridNo[gridPosition];
      const { tileSize } = promo;
      updatedPPLP = promoHandler(
        cid,
        tileSize,
        promo,
        gridPosition,
        updatedPPLP,
        i,
        pageCategoryId,
        gridBlockRef,
        processedBreadCrumbs,
        titleTextCount
      );
      i += tileSize;
    } else if (currentCrossGridNo && !updatedPPLP[i] && currentCrossGridNo[gridPosition]) {
      updatedPPLP[i] = (
        <GridPromo
          key={`${cid}-gp-${i}`}
          promoObj={currentCrossGridNo[gridPosition]}
          variation="horizontal"
          numberOfProducts
        />
      );
      i += 1;
    } else if (paginatedProducts[k].isTitle) {
      if (isRemoveCategoryBreakEnabled) {
        k += 1;
        const { id, name } = paginatedProducts[k] || {};
        categoryData = `${id} | ${name}`;
      } else {
        const { id, name } = paginatedProducts[k] || {};
        categoryData = `${id} | ${name}`;
        updatedPPLP[i] = (
          <Heading
            key={`${cid}-${id}-${name}-${i}`}
            className={itemTitle}
            fontFamily="secondaryFontFamily"
          >
            {isPLPSortAndOneHrFilterRedesignEnabled && <HorizontalBar className="horizontal-bar" />}
            {name}
            <HorizontalBar className="horizontal-bar" />
          </Heading>
        );
        titleCount += 1;
        titleTextCount += 1;
        titleIndexesArray = [...titleIndexesArray, i];
        i += 1;
        k += 1;
      }
    } else if (paginatedProducts[k].isESpotPlpContent) {
      const { eSpotCatGrpId, provider, linkedImage } = paginatedProducts[k] || {};
      const uniqueId = `${cid}-${eSpotCatGrpId}`;
      updatedPPLP[i] = (
        <ModuleDynamic
          provider={provider}
          linkedImage={linkedImage}
          uniqueId={uniqueId}
          eSpotPromoKey={`${uniqueId}-${i}`}
          isPLPEspot
          gridPosition={i}
          gridBlockRef={gridBlockRef}
          titleIndexesArray={titleIndexesArray}
          currentRowNo={currentRowNo}
          isFullWidthInGridPromoHidden={isFullWidthInGridPromoHidden}
          currentPageNumber={currentPageNumber}
          recordsPerPage={recordsPerPage}
        />
      );

      titleCount += 1;

      i += 1;
      k += 1;
    } else if (
      gridColumn &&
      heroSlotRow?.length > 0 &&
      isPLPDynamicGridHeroEnabled &&
      !isNewPLPAtbCardTileEnabled &&
      !isPaginatedAlternatePlpEnabled &&
      heroSlotRow?.length * 2 > totalProductHero &&
      paginatedProducts[k].isHeroProduct &&
      !titleCount > 0
    ) {
      if (!heroProductArray?.includes(categoryData)) heroProductArray.push(categoryData);
      heroProductArray.push({ ...paginatedProducts[k] });
      totalProductHero += 1;
      k += 1;
    } else {
      const sharedProps = {
        pathName,
        tileData: { ...paginatedProducts[k], categoryData },
        tileActions,
        wishListData,
        isBrierleyPromoEnabled,
        submitButtonText: renderAddToBagLabel(isBopisFilterOn),
        isShortImage,
        promotionsData,
        serverTime,
        trackClickPromotion,
        isBuyMorePromoEnabled,
        trackWishListClick,
        isLoggedIn,
        saveProductDataForPdp,
        saveProductDataForQV,
        categoryName,
        isPlcc,
        isInternationalShipping,
        isTopRatedBackAbEnabled,
        isBestSellerBackAbEnabled,
        isSyteEnabled,
        trackAnalyticsClick: trackClickPromotion,
        isPaginatorClicked,
        isNewSwatchUrlEnabled,
        isNewPLPImageBorderEnabled,
        isAlternatePlpEnabled,
        isPaginatedAlternatePlpEnabled,
        isBopisFilterOn,
        isOneClickATCEnabled,
        isHideOriginalPriceAndMarchantTagEnabled,
        isInfluencerBadgeEnabled,
        isTileRedesignAB,
        imageConfigWithoutDPR,
        isFeatureBrandEnabled,
      };

      const renderTileView = () => {
        if (isNewPLPAtbCardTileEnabled) {
          return (
            <PjpPlpTileView
              {...sharedProps}
              preloadImage={i <= 2}
              isMostFavoritedBadgeEnabled={isMostFavoritedBadgeEnabled}
              setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}
              isTileRedesignAB={isTileRedesignAB}
              isInfluencerPicksEnabled={isInfluencerPicksEnabled}
              favAddRemoveLoadingState={favAddRemoveLoadingState}
              isShowPriceRangeEnabled={isShowPriceRangeEnabled}
              isAlternateImageOnHoverEnabled={isAlternateImageOnHoverEnabled}
            />
          );
        }
        if (isPaginatedAlternatePlpEnabled) {
          return <AltPlpTile {...sharedProps} />;
        }
        return (
          <PlpTileView
            {...sharedProps}
            preloadImage={i <= 2}
            setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}
            isMostFavoritedBadgeEnabled={isMostFavoritedBadgeEnabled}
            isTileRedesignAB={isTileRedesignAB}
            isInfluencerPicksEnabled={isInfluencerPicksEnabled}
            favAddRemoveLoadingState={favAddRemoveLoadingState}
            isShowPriceRangeEnabled={isShowPriceRangeEnabled}
            isAlternateImageOnHoverEnabled={isAlternateImageOnHoverEnabled}
            isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
            onHeightChange={handleHeightChange}
            isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
            isHoverToPreviewColorSwatchesEnabled={isHoverToPreviewColorSwatchesEnabled}
            isInteractiveColorSwatchBandEnabled={isInteractiveColorSwatchBandEnabled}
            isHoverToRevealATBButtonEnabled={isHoverToRevealATBButtonEnabled}
            isSwatchAsColorUrlEnabled={isSwatchAsColorUrlEnabled}
            isFinalSaleABEnabled={isFinalSaleABEnabled}
            isRandomBgColorEnabled={isRandomBgColorEnabled}
            productIndex={productIndex}
            isDynamicBadgeEnabledAbtest={isDynamicBadgeEnabledAbtest}
            isFadeImageHoverEnabled={isFadeImageHoverEnabled}
          />
        );
      };

      updatedPPLP[i] = (
        <PlpTileContianer
          key={`plp-tile-${cid}-${currentPageNumber}-${i}`}
          isPaginatedAlternatePlpEnabled={isPaginatedAlternatePlpEnabled}
          isFourTileGridEnabled={isFourTileGridEnabled}
          className={`${
            !isNewPLPAtbCardTileEnabled && !isPaginatedAlternatePlpEnabled
              ? `${plpTileClassName}`
              : ''
          } plpTileView`}
        >
          {renderTileView()}
        </PlpTileContianer>
      );
      i += 1;
      k += 1;
      productIndex += 1;
    }
  }

  if (additionalProductsCountOnCsr > 0) {
    const renderedProductCount = updatedPPLP.length;
    for (
      let i = renderedProductCount;
      i < additionalProductsCountOnCsr + renderedProductCount;
      i += 1
    ) {
      updatedPPLP[i] = <StyledPaginatedSkeleton key={`plp-tile-skeleton-${i}`} />;
    }
  }

  if (
    currentRowNo &&
    Object.keys(currentRowNo).length > 0 &&
    !isFullWidthInGridPromoHidden &&
    !hidePromoOnFilterApplied
  ) {
    const indexs = Object.keys(currentRowNo);
    const { fullRowHeight } = getInGridFullRowHeight(gridBlockRef);
    let isLastRowAdjustmentDone = false;
    for (let i = 0; i < indexs.length; i += 1) {
      const index = parseInt(indexs[i], 10) - (currentPageNumber - 1) * recordsPerPage;
      const promo = currentRowNo[indexs[i]] || {};
      const { tileSize, provider = '', IS_NON_CLICKABLE_VIEW = false } = promo || {};
      const isFullWidthTile = tileSize > 2;
      const doubleTileClassName = getDoubleTileClassName(provider, IS_NON_CLICKABLE_VIEW);
      // Create a promo component
      const promoComponent = (
        <VertcalPromoGrid
          key={`${cid}-full-width-promo-grid-${index}`}
          className={`${
            isFullWidthTile ? 'fullWidthTile' : ''
          } fullwidth-mb ${doubleTileClassName}`}
          columnCount={tileSize}
          isFullWidthTile={isFullWidthTile}
          tileHeight={tileHeight}
        >
          <GridPromo
            promoObj={promo}
            variation="full-width"
            isInGridPromo
            pageCategoryId={pageCategoryId}
            tileHeightValue={fullRowHeight}
            isFullWidthTile
            processedBreadCrumbs={processedBreadCrumbs}
          />
        </VertcalPromoGrid>
      );

      const eSpotCount = (updatedPPLP || [])
        .slice(0, index)
        .filter((item) => item?.props?.provider?.toLowerCase() === 'espot_plp').length;
      const titleCount = (updatedPPLP || [])
        .slice(0, index)
        .filter((item) => item?.props?.className?.toLowerCase() === itemTitle).length;
      const titleIndexes = (updatedPPLP || [])
        .map((item, ind) => (item?.props?.className?.toLowerCase() === itemTitle ? ind : null))
        .filter((ind) => ind !== null);

      // Create an array of empty divs based on the tileSize
      const emptyDivs =
        tileSize > 1
          ? Array(tileSize - 1)
              .fill()
              .map((_, j) => (
                <div
                  className={'empty-div-full-width-row'}
                  key={`empty-div-${index + j}`}
                  data-index={index + j}
                />
              ))
          : [];

      // Adjust the index based on the occurrence of eSpot promos
      let adjustedIndex = index;
      if (!isRemoveCategoryBreakEnabled) {
        adjustedIndex = eSpotCount > 0 ? index + eSpotCount : adjustedIndex;
        adjustedIndex = titleCount > 0 ? adjustedIndex + titleCount : adjustedIndex;
      }
      const nearestCategoryStartingIndex = (() => {
        const filteredIndexes = titleIndexes.filter((ind) => ind < adjustedIndex);
        return filteredIndexes.length === 0 ? 0 : Math.max(...filteredIndexes) + 1;
      })();

      if (nearestCategoryStartingIndex < adjustedIndex && titleCount !== 0) {
        adjustedIndex =
          nearestCategoryStartingIndex +
          Math.floor((adjustedIndex - nearestCategoryStartingIndex) / tileSize) * tileSize;
      }

      if (adjustedIndex > updatedPPLP?.length && !isLastRowAdjustmentDone) {
        const { startingIndex, indexAdjustmentNeeded } = checkForStartingRowIndex({
          adjustedIndex,
          tileSize,
          updatedPPLPLength: updatedPPLP?.length,
          nearestCategoryStartingIndex,
        });

        if (!startingIndex) {
          const emptyDivsTobeInsertedBefore =
            tileSize > 1
              ? Array(indexAdjustmentNeeded)
                  .fill()
                  .map((_, j) => (
                    <div
                      className={'empty-div-full-width-row'}
                      key={`empty-div-last-row${index + j}`}
                      data-index={index + j}
                    />
                  ))
              : [];
          isLastRowAdjustmentDone = true;
          updatedPPLP.push(...emptyDivsTobeInsertedBefore);
        }
      }

      // Insert promo followed by empty divs at the adjusted index
      updatedPPLP.splice(adjustedIndex, 0, promoComponent, ...emptyDivs);
    }
  }

  if (
    heroProductArray?.length > 0 &&
    !isPaginatedAlternatePlpEnabled &&
    !isNewPLPAtbCardTileEnabled
  ) {
    let tileCategoryData = categoryData;
    let gridItem = 0;
    let itemRow = 1;
    let plpTitleCount = 0;
    const updatedPPLPIndex = [];

    // 1. Collect indexes for hero tile injection based on row rules
    const newupdatedPPLP = [...updatedPPLP];
    for (let ind = 0; ind < newupdatedPPLP.length; ind += 1) {
      const item = newupdatedPPLP[ind];
      const isSkip = item?.props?.className?.toLowerCase() === itemTitle;
      if (isSkip) {
        plpTitleCount += 1;
        break;
      }

      if (!isSkip) gridItem += 1;
      if (gridItem > 1 && gridItem % gridColumn === 1) itemRow += 1;

      if (heroSlotRow.includes(itemRow)) {
        newupdatedPPLP.splice(ind, 0, null, null, null, null);
        heroSlotRow.shift();
        updatedPPLPIndex.push(ind);
      }
    }

    if (plpTitleCount > 0) {
      return updatedPPLP;
    }
    // 2. Reusable helper to extract up to 2 hero items
    const extractHeroItems = () => {
      const items = [];
      const indexesToRemove = new Set();

      (heroProductArray || []).some((stock, index) => {
        if (typeof stock === 'string') {
          tileCategoryData = stock;
          return false;
        }
        if (items.length < 2) {
          items.push(stock);
          indexesToRemove.add(index);
          return false;
        }
        return true;
      });

      heroProductArray = heroProductArray.filter((_, idx) => !indexesToRemove.has(idx));
      return items;
    };

    // 3. Helper to generate tile props and wrapper
    const getHeroTile = (heroProduct, indexKey, preload = false) => {
      const sharedProps = {
        pathName,
        tileData: { ...heroProduct, categoryData: tileCategoryData },
        tileActions,
        wishListData,
        isBrierleyPromoEnabled,
        submitButtonText: renderAddToBagLabel(isBopisFilterOn),
        isShortImage,
        promotionsData,
        serverTime,
        trackClickPromotion,
        isBuyMorePromoEnabled,
        trackWishListClick,
        isLoggedIn,
        saveProductDataForPdp,
        saveProductDataForQV,
        categoryName,
        isPlcc,
        isInternationalShipping,
        isTopRatedBackAbEnabled,
        isBestSellerBackAbEnabled,
        isSyteEnabled,
        trackAnalyticsClick: trackClickPromotion,
        isPaginatorClicked,
        isNewSwatchUrlEnabled,
        isNewPLPImageBorderEnabled,
        isAlternatePlpEnabled,
        isPaginatedAlternatePlpEnabled,
        isBopisFilterOn,
        isOneClickATCEnabled,
        isHideOriginalPriceAndMarchantTagEnabled,
        isInfluencerBadgeEnabled,
        isTileRedesignAB,
        imageConfigWithoutDPR,
        isFeatureBrandEnabled,
      };
      if (!heroProduct || Object.keys(heroProduct).length === 0) return null;
      return (
        <PlpTileContianer
          key={`plp-tile-${cid}-${currentPageNumber}-${totalRcd + indexKey}`}
          isPaginatedAlternatePlpEnabled={isPaginatedAlternatePlpEnabled}
          isFourTileGridEnabled={isFourTileGridEnabled}
          className={`${
            !isNewPLPAtbCardTileEnabled && !isPaginatedAlternatePlpEnabled ? plpTileClassName : ''
          } plp-tile-container hero-tile-container hide-hero-tile`}
        >
          <HeroTileView
            {...sharedProps}
            preloadImage={preload}
            setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}
            isMostFavoritedBadgeEnabled={isMostFavoritedBadgeEnabled}
            isTileRedesignAB={isTileRedesignAB}
            isInfluencerPicksEnabled={isInfluencerPicksEnabled}
            favAddRemoveLoadingState={favAddRemoveLoadingState}
            isShowPriceRangeEnabled={isShowPriceRangeEnabled}
            isAlternateImageOnHoverEnabled={isAlternateImageOnHoverEnabled}
            isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
            onHeightChange={handleHeightChange}
            isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
            isHoverToPreviewColorSwatchesEnabled={isHoverToPreviewColorSwatchesEnabled}
            isInteractiveColorSwatchBandEnabled={isInteractiveColorSwatchBandEnabled}
            isHoverToRevealATBButtonEnabled={isHoverToRevealATBButtonEnabled}
            isSwatchAsColorUrlEnabled={isSwatchAsColorUrlEnabled}
          />
        </PlpTileContianer>
      );
    };

    // 4. Insert hero tiles at identified row indexes
    updatedPPLPIndex.forEach((ind) => {
      const items = extractHeroItems();
      let insertIndex = ind;

      items.forEach((heroProduct) => {
        const tile = getHeroTile(heroProduct, insertIndex, false);

        if (items.length === 1 && gridColumn === 4) {
          updatedPPLP.splice(
            insertIndex,
            0,
            <div
              className="hide-on-mobile hide-hero-tile"
              key={`empty-div-before-${uuidv4()}`}
              data-index={ind}
            />,
            tile,
            <div
              className="hide-on-mobile hide-hero-tile"
              key={`empty-div-after-${uuidv4()}`}
              data-index={ind}
            />
          );
        } else {
          updatedPPLP.splice(
            insertIndex,
            0,
            tile,
            <div className="is-hidden" key={`empty-div-last-${uuidv4()}`} data-index={ind} />
          );
        }

        insertIndex += 2;
      });
    });

    // 5. Handle remaining rows/hero items at end
    if (heroSlotRow.length > 0 && heroProductArray?.length > 0) {
      // checking if there is any space at last of the PLP row and then adding epmty div
      const grid = updatedPPLP.length - plpTitleCount;
      const addindDiv = gridColumn - (grid % gridColumn);
      if (addindDiv !== gridColumn) {
        const emptyDivsTobeInsertedBefore = Array(addindDiv)
          .fill()
          .map(() => <div className="" key={`empty-div-last-${uuidv4()}`} />);
        updatedPPLP.push(...emptyDivsTobeInsertedBefore);
      }

      let lastIndex = updatedPPLP.length;

      heroSlotRow.forEach(() => {
        const items = extractHeroItems();
        items.forEach((heroProduct) => {
          const tile = getHeroTile(heroProduct, lastIndex);
          if (items.length === 1 && gridColumn === 4) {
            updatedPPLP.splice(
              lastIndex,
              0,
              <div
                className="hide-on-mobile hide-hero-tile"
                key={`empty-div-before-${uuidv4()}`}
              />,
              tile,
              <div className="hide-on-mobile hide-hero-tile" key={`empty-div-after-${uuidv4()}`} />
            );
          } else {
            updatedPPLP.splice(lastIndex, 0, tile);
          }
          lastIndex += 1;
        });
      });
    }
  }

  return updatedPPLP;
}

PaginatedView.propTypes = {
  pageNumber: PropTypes.number,
  paginationObj: PropTypes.arrayOf(),
  totalProductsCount: PropTypes.number,
  recordsPerPage: PropTypes.number,
  paginatedProducts: PropTypes.arrayOf(),
  onQuickViewOpenClick: PropTypes.func,
  onAddItemToFavorites: PropTypes.func,
  onSetLastDeletedItemIdAction: PropTypes.func,
  wishListData: PropTypes.shape({}),
  plpGridPromos: PropTypes.arrayOf(),
  isBrierleyPromoEnabled: PropTypes.bool,
  isShortImage: PropTypes.bool,
  submitSortChange: PropTypes.func,
  saveProductDataForPdp: PropTypes.func,
  navTree: PropTypes.shape({}),
  isBopisFilterOn: PropTypes.bool,
  processedBreadCrumbs: PropTypes.arrayOf(),
  isPaginatedPlpLoading: PropTypes.bool,
  isInitialCall: PropTypes.bool,
  isL2Category: PropTypes.bool,
  pathName: PropTypes.string,
  setUpdatedUrlActn: PropTypes.func,
  isPlcc: PropTypes.bool,
  isInternationalShipping: PropTypes.bool.isRequired,
  isShowL2Skeleton: PropTypes.bool,
  isTopRatedBackAbEnabled: PropTypes.bool,
  isBestSellerBackAbEnabled: PropTypes.bool,
  isNewPLPAtbCardTileEnabled: PropTypes.bool,
  isAlternatePlpEnabled: PropTypes.bool,
  isPaginatedAlternatePlpEnabled: PropTypes.bool,
  isNewSwatchUrlEnabled: PropTypes.bool,
  currentNavIds: PropTypes.arrayOf(),
  isOneClickATCEnabled: PropTypes.bool,
  addToBagEcom: PropTypes.func,
  isMostFavoritedBadgeEnabled: PropTypes.bool,
  setPLPSLPAddToCartCTA: PropTypes.string,
  isTileRedesignAB: PropTypes.bool,
  isInfluencerPicksEnabled: PropTypes.bool,
  additionalProductsCountOnCsr: PropTypes.number,
  isAlternateImageOnHoverEnabled: PropTypes.bool,
  isShowPriceRangeEnabled: PropTypes.bool,
};

PaginatedView.defaultProps = {
  pageNumber: 1,
  paginationObj: [],
  totalProductsCount: -1,
  recordsPerPage: 30,
  paginatedProducts: [],
  onQuickViewOpenClick: () => {},
  onAddItemToFavorites: () => {},
  onSetLastDeletedItemIdAction: () => {},
  wishListData: {},
  plpGridPromos: [],
  isBrierleyPromoEnabled: false,
  isShortImage: false,
  submitSortChange: () => {},
  saveProductDataForPdp: () => {},
  navTree: {},
  isBopisFilterOn: false,
  processedBreadCrumbs: [],
  isPaginatedPlpLoading: false,
  isInitialCall: false,
  isL2Category: false,
  pathName: '',
  setUpdatedUrlActn: () => {},
  isPlcc: false,
  isShowL2Skeleton: false,
  isTopRatedBackAbEnabled: false,
  isNewSwatchUrlEnabled: false,
  isBestSellerBackAbEnabled: false,
  isNewPLPAtbCardTileEnabled: false,
  currentNavIds: [],
  isAlternatePlpEnabled: false,
  isPaginatedAlternatePlpEnabled: false,
  isOneClickATCEnabled: false,
  addToBagEcom: () => {},
  isMostFavoritedBadgeEnabled: false,
  setPLPSLPAddToCartCTA: null,
  isTileRedesignAB: false,
  isInfluencerPicksEnabled: false,
  additionalProductsCountOnCsr: 0,
  isAlternateImageOnHoverEnabled: false,
  isShowPriceRangeEnabled: false,
};
